import { PANEL_CATALOG, type PanelDef } from "../config/panelCatalog";
import type { PanelPermission } from "../api/access";

export const NAV_PANEL_COUNT = PANEL_CATALOG.filter((p) => !p.feature).length;

export const enabledCount = (perms?: Record<string, PanelPermission>) =>
  PANEL_CATALOG.filter((p) => !p.feature && perms?.[p.key]?.visible).length;

const FREE_PLAN_ROOT_KEYS = new Set([
  "dashboard",
  "employees",
  "attendance",
  "leaves",
  "tasks",
  "todo",
  "downloads",
  "work-schedule",
  "activity-reports",
  "activity-comparison",
  // Opt-in, defaultVisible:false in PANEL_CATALOG — but it must still appear
  // in the Starter/limited-plan grid so an admin CAN grant it. Both apps'
  // routing already treat /leo as usable in limited-workspace mode (see
  // chat_temlio/src/utils/limitedAccess.ts and accessPanels.ts); omitting it
  // here just meant the toggle never rendered, so it could never be turned on.
  "leo",
  // Same reasoning as "leo" above: defaultVisible:false in PANEL_CATALOG, so
  // it is off until granted — but the toggle has to RENDER on the limited plan
  // or it could never be granted there at all. A rollout control that can't be
  // reached on a plan is just the feature being unavailable on that plan.
  "attendance-face",
]);

export const panelCatalogForPlan = (limited: boolean): PanelDef[] =>
  limited
    ? PANEL_CATALOG.filter(
        (panel) =>
          FREE_PLAN_ROOT_KEYS.has(panel.key) ||
          (!!panel.feature && !!panel.parent && FREE_PLAN_ROOT_KEYS.has(panel.parent))
      )
    : PANEL_CATALOG;

export const enabledCountForCatalog = (
  perms: Record<string, PanelPermission> | undefined,
  catalog: PanelDef[]
) => catalog.filter((panel) => !panel.feature && perms?.[panel.key]?.visible).length;

export const restrictPermissionsToCatalog = (
  permissions: Record<string, PanelPermission>,
  catalog: PanelDef[]
): Record<string, PanelPermission> => {
  const allowed = new Set(catalog.map((panel) => panel.key));
  return Object.fromEntries(
    Object.entries(permissions).map(([key, permission]) => [
      key,
      allowed.has(key) ? permission : { ...permission, visible: false },
    ])
  );
};

// Every panel visible at Manage level — the effective access an UNGOVERNED
// user has (the permissive role-based fallback lets them see/manage everything).
// Used to seed the superadmin grid so it opens reflecting the admin's real
// current access rather than catalog defaults.
export const fullAccessPermissions = (): Record<string, PanelPermission> => {
  const out: Record<string, PanelPermission> = {};
  for (const p of PANEL_CATALOG) {
    out[p.key] = {
      visible: true,
      level: p.forceView ? (p.defaultLevel ?? "view") : "manage",
    };
  }
  return out;
};

export const blankPermissions = (
  existing?: Record<string, PanelPermission>
): Record<string, PanelPermission> => {
  const out: Record<string, PanelPermission> = {};
  for (const p of PANEL_CATALOG) {
    // forceView panels are always visible for every role; keep any Manage level
    // the profile already granted, but never let View be turned off.
    if (p.forceView) {
      out[p.key] = {
        visible: true,
        level: existing?.[p.key]?.level ?? p.defaultLevel ?? "view",
      };
      continue;
    }
    out[p.key] =
      existing?.[p.key] ?? {
        visible: p.defaultVisible ?? !!p.feature,
        level: p.defaultLevel ?? "view",
      };
  }
  return out;
};
